import { GameState, getCurrentPlayer } from './GameState';
import { Card, isWild } from '../utils/CardUtils';
import { Meld, validateMeld, canAddToMeld, isBurraco } from './Meld';

export function canDrawFromDeck(state: GameState): boolean {
  return state.turnPhase === 'draw' && state.drawPile.length > 0;
}

export function canDrawFromDiscard(state: GameState): boolean {
  return state.turnPhase === 'draw' && state.discardPile.length > 0;
}

export function canMeld(state: GameState): boolean {
  return state.turnPhase === 'meld' || (state.turnPhase === 'draw' && state.hasDrawnThisTurn);
}

export function canDiscard(state: GameState): boolean {
  return (state.turnPhase === 'meld' || state.turnPhase === 'discard') && state.hasDrawnThisTurn;
}

export function canDiscardCard(state: GameState, card: Card): boolean {
  if (!canDiscard(state)) return false;
  const player = getCurrentPlayer(state);

  // Check if this is going out
  if (player.hand.length === 1) {
    // Going out: last card cannot be wild
    if (isWild(card)) return false;
    // Must have taken pozzetto and have at least one burraco
    if (!player.tookPozzetto) return false;
    if (!player.melds.some(m => isBurraco(m))) return false;
  }

  // If player has taken pozzetto but has no burraco and only 2 cards left,
  // they must keep at least 2 cards (one to discard, one to hold)
  if (player.tookPozzetto && !player.melds.some(m => isBurraco(m)) && player.hand.length <= 2) {
    // Can still discard but can't go out without a burraco
    // If they'd have 0 cards after discard, that's going out — blocked above
    // If they'd have 1 card, that's fine (they keep playing)
  }

  return true;
}

export function canPlayMeld(state: GameState, cardIds: string[]): boolean {
  if (!canMeld(state)) return false;
  const player = getCurrentPlayer(state);
  const cards = cardIds.map(id => player.hand.find(c => c.id === id)).filter((c): c is Card => c !== undefined);
  if (cards.length !== cardIds.length) return false;
  return validateMeld(cards) !== null;
}

export function canAddCardToMeld(state: GameState, cardId: string, meldId: string): boolean {
  if (!canMeld(state)) return false;
  const player = getCurrentPlayer(state);
  const card = player.hand.find(c => c.id === cardId);
  if (!card) return false;
  const meld = player.melds.find(m => m.id === meldId);
  if (!meld) return false;
  return canAddToMeld(meld, card);
}

export function canGoOut(state: GameState): boolean {
  const player = getCurrentPlayer(state);
  if (!player.tookPozzetto) return false;
  if (!player.melds.some(m => isBurraco(m))) return false;
  // Player must have exactly 1 card (which they'll discard)
  // and that card must not be wild
  if (player.hand.length !== 1) return false;
  if (isWild(player.hand[0])) return false;
  return true;
}

export function shouldEndRound(state: GameState): 'going_out' | 'deck_empty' | 'stalemate' | null {
  // Deck reduced to 2 cards — checked after drawing the 3rd-to-last
  if (state.drawPile.length <= 2 && state.turnPhase !== 'draw') {
    return 'deck_empty';
  }

  // Stalemate: 4 consecutive turns of just taking opponent's single discard
  if (state.stalemateTurnCount >= 4) {
    return 'stalemate';
  }

  return null;
}
